import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Volume2, VolumeX } from "lucide-react";
import {
  CHEER_EMOJIS,
  CHEER_SOUNDS,
  cheerDelayMs,
  cheerEmoji,
  cheerSoundsMuted,
  clearCheerMutes,
  isCheerKind,
  playCheerSound,
  preloadCheerSounds,
  toggleCheerSoundsMuted,
  unlockCheerAudio,
} from "../../utils/championshipCheer";
import "./ChampionshipCheerBar.css";

const COOLDOWN_MS = 1200;

export default function ChampionshipCheerBar({
  incoming,
  onSend,
  pointId = "",
  controls = true,
}) {
  const { t } = useTranslation();
  const [floats, setFloats] = useState([]);
  const [notice, setNotice] = useState("");
  const [muted, setMuted] = useState(() => cheerSoundsMuted());
  const lockedUntil = useRef(0);
  const seen = useRef(null);

  useEffect(() => {
    void preloadCheerSounds();
    const unlock = () => unlockCheerAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  const seenPoint = useRef(pointId);
  useEffect(() => {
    if (seenPoint.current === pointId) return;
    seenPoint.current = pointId;
    clearCheerMutes();
    setMuted(false);
  }, [pointId]);

  useEffect(() => {
    if (!incoming || incoming === seen.current) return;
    seen.current = incoming;
    const kind = String(incoming.kind || "");
    if (!isCheerKind(kind) && kind !== "fanfare") return;
    let cancelled = false;
    void preloadCheerSounds().finally(() => {
      if (cancelled) return;
      const delay = cheerDelayMs(incoming.play_at);
      if (delay < -8000) return;
      const emoji = cheerEmoji(kind) || (kind === "fanfare" ? "🎺" : "");
      const show = () => {
        if (!emoji) return;
        const id = `${Date.now()}-${Math.random()}`;
        const left = 12 + Math.random() * 68;
        setFloats((prev) => [...prev.slice(-12), { id, emoji, left }]);
        window.setTimeout(() => {
          setFloats((prev) => prev.filter((item) => item.id !== id));
        }, 1400);
      };
      if (delay > 50) window.setTimeout(show, delay);
      else show();
      if (incoming.audible !== false) playCheerSound(kind, delay);
    });
    return () => {
      cancelled = true;
    };
  }, [incoming]);

  const send = async (kind) => {
    if (Date.now() < lockedUntil.current) return;
    lockedUntil.current = Date.now() + COOLDOWN_MS;
    setNotice("");
    try {
      await unlockCheerAudio();
      const ack = await onSend(kind);
      if (ack && ack.ok === false) {
        if (ack.code !== "rate_limit") lockedUntil.current = 0;
        setNotice(
          ack.code === "rate_limit"
            ? t("cmCheerWait", "Wait a moment before the next cheer")
            : t("cmCheerFailed", "Could not send that cheer")
        );
      }
    } catch {
      lockedUntil.current = 0;
      setNotice(t("cmCheerFailed", "Could not send that cheer"));
    }
  };

  const flipMute = () => {
    setMuted(toggleCheerSoundsMuted());
  };

  return (
    <>
      <div className="cm-cheer-float" aria-hidden>
        {floats.map((item) => (
          <span
            key={item.id}
            className="cm-cheer-float__emoji"
            style={{ left: `${item.left}%` }}
          >
            {item.emoji}
          </span>
        ))}
      </div>
      {controls ? (
      <div className="cm-cheer" role="region" aria-label={t("cmCheerSounds", "Sounds")}>
        <div className="cm-cheer__bar">
        <div className="cm-cheer__row">
          <span className="cm-cheer__label">{t("cmCheerSounds", "Sounds")}</span>
          <span className="cm-cheer__group cm-cheer__group--sounds">
            {CHEER_SOUNDS.map((item) => (
              <button
                key={item.kind}
                type="button"
                className={`cm-cheer__btn${muted ? " is-muted" : ""}`}
                aria-label={t(item.labelKey, item.label)}
                onClick={() => void send(item.kind)}
              >
                {item.emoji}
              </button>
            ))}
          </span>
          <span className="cm-cheer__label">{t("cmCheerEmojis", "Emojis")}</span>
          <span className="cm-cheer__group cm-cheer__group--emojis">
            {CHEER_EMOJIS.map((item) => (
              <button
                key={item.kind}
                type="button"
                className="cm-cheer__btn"
                aria-label={t(item.labelKey, item.label)}
                onClick={() => void send(item.kind)}
              >
                {item.emoji}
              </button>
            ))}
          </span>
        </div>
        <button
          type="button"
          className={`cm-cheer__mute-all${muted ? " is-on" : ""}`}
          aria-pressed={muted}
          aria-label={
            muted
              ? t("cmCheerUnmute", "Unmute")
              : t("cmCheerMuteUntilPoint", "Mute until the next point")
          }
          title={
            muted
              ? t("cmCheerUnmute", "Unmute")
              : t("cmCheerMuteUntilPoint", "Mute until the next point")
          }
          onClick={flipMute}
        >
          {muted ? (
            <VolumeX size={20} strokeWidth={2.4} aria-hidden />
          ) : (
            <Volume2 size={20} strokeWidth={2.4} aria-hidden />
          )}
        </button>
        </div>
        {notice ? <p className="cm-cheer__notice">{notice}</p> : null}
      </div>
      ) : null}
    </>
  );
}
